// Generated from scripts/product-price.ts. Rebuild with pnpm scripts:build.
import { Component } from '@theme/component';
import { TextComponent } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';
function isProductSelect(event) {
    return 'promise' in event && event.promise instanceof Promise;
}
function isAbort(error) {
    return error instanceof Error && error.name === 'AbortError';
}
function elementOf(ref) {
    return ref instanceof Element ? ref : undefined;
}
/**
 * A product price. It follows the variant picker of its section (or dialog): while Shopify renders the section for
 * a new selection the price shows the loading shimmer, and when the section arrives the price container is
 * replaced with the one it rendered, so the price, the regular price, the unit price and the discount percent are
 * always Shopify's. Product cards update their own price and are skipped here.
 *
 * Contract kept from the inherited module: the `product-price` element, the `priceContainer` and
 * `volumePricingNote` refs, `data-block-id` (how the new markup is found) and `data-product-id`, and the update of
 * the installments form's variant id, which makes the payment terms banner render again.
 */
class ProductPrice extends Component {
    #lifetime;
    /** Counts the selections seen, so only the newest one decides when the shimmer stops. */
    #selection = 0;
    connectedCallback() {
        super.connectedCallback();
        this.#lifetime?.abort();
        const section = this.closest('.shopify-section, dialog');
        if (!section)
            return;
        const lifetime = new AbortController();
        this.#lifetime = lifetime;
        section.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal: lifetime.signal });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.#lifetime?.abort();
        this.#lifetime = undefined;
    }
    /** Starts the shimmer on every price text: it ends when the recalculated container replaces them. */
    #shimmer() {
        for (const text of this.querySelectorAll('text-component')) {
            if (text instanceof TextComponent)
                text.shimmer();
        }
    }
    #stopShimmer() {
        for (const text of this.querySelectorAll('text-component'))
            text.removeAttribute('shimmer');
    }
    #handleProductSelect = (event) => {
        if (!isProductSelect(event) || !(event.target instanceof Element) || event.target.closest('product-card'))
            return;
        const selection = ++this.#selection;
        this.#shimmer();
        event.promise
            .then(({ detail }) => {
            if (!detail?.html) {
                this.#stopShimmer();
                return;
            }
            const { html, newProduct } = detail;
            if (newProduct) {
                this.dataset.productId = newProduct.id;
            }
            else if (detail.productId && detail.productId !== this.dataset.productId) {
                this.#stopShimmer();
                return;
            }
            const priceContainer = elementOf(this.refs.priceContainer);
            const volumePricingNote = elementOf(this.refs.volumePricingNote);
            // Find the new product-price element in the updated HTML
            const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
            if (!newProductPrice) {
                this.#stopShimmer();
                return;
            }
            // Update price container
            const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
            if (newPrice && priceContainer)
                priceContainer.replaceWith(newPrice);
            // Update volume pricing note
            const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
            if (!newNote) {
                volumePricingNote?.remove();
            }
            else if (!volumePricingNote) {
                // Use newPrice since priceContainer was just replaced and now points to the detached element
                newPrice?.insertAdjacentElement('afterend', newNote.cloneNode(true));
            }
            else {
                volumePricingNote.replaceWith(newNote);
            }
            // Update installments (SPI banner) variant ID to trigger payment terms re-render
            const installmentsInput = this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`);
            if (installmentsInput) {
                installmentsInput.value = detail.resource?.id?.toString() ?? '';
                installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
        })
            .catch((error) => {
            // A newer selection aborted this one and has taken over the shimmer.
            if (selection === this.#selection)
                this.#stopShimmer();
            if (!isAbort(error))
                console.warn('[product-price] Event promise rejected:', error);
        });
    };
}
if (!customElements.get('product-price')) {
    customElements.define('product-price', ProductPrice);
}
